import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MailCheck, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-2xl border-2 border-[color:var(--line)] bg-white px-4 py-3 text-lg outline-none transition-colors focus:border-[color:var(--terracotta)]";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="forgot-password-page" className="mx-auto max-w-lg px-4 py-14 sm:px-8">
      <h1 className="rise font-display text-4xl font-bold">Mot de passe oublié</h1>
      {sent ? (
        <div data-testid="forgot-password-sent" className="rise card-paper mt-8 p-7">
          <MailCheck size={36} className="text-[color:var(--sage)]" />
          <p className="mt-3 text-lg font-semibold">Si un compte existe avec l'adresse {email}, vous allez recevoir un e-mail avec un lien pour choisir un nouveau mot de passe.</p>
          <p className="mt-2 text-sm text-[color:var(--muted-ink)]">Le lien est valable 1 heure. Pensez à regarder dans les courriers indésirables.</p>
          <Link to="/connexion" className="btn-ghost mt-6">Retour à la connexion</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="rise card-paper mt-8 space-y-4 p-7">
          <p className="text-[color:var(--muted-ink)]">Indiquez l'e-mail de votre compte parent. Nous vous enverrons un lien pour choisir un nouveau mot de passe.</p>
          <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email du parent" className={inputCls} />
          {error && <p data-testid="forgot-error" className="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700">{error}</p>}
          <button data-testid="forgot-submit-button" disabled={busy} className="btn-primary w-full">{busy ? "Envoi…" : "Recevoir le lien"}</button>
        </form>
      )}
    </main>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const token = params.get("token");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError("Les deux mots de passe ne sont pas identiques."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/connexion"), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="reset-password-page" className="mx-auto max-w-lg px-4 py-14 sm:px-8">
      <h1 className="rise font-display text-4xl font-bold">Nouveau mot de passe</h1>
      {!token ? (
        <p data-testid="reset-missing-token" className="card-paper mt-8 p-7">Lien incomplet. <Link to="/mot-de-passe-oublie" className="font-bold underline">Demander un nouveau lien</Link></p>
      ) : done ? (
        <div data-testid="reset-success" className="rise card-paper mt-8 p-7">
          <KeyRound size={36} className="text-[color:var(--sage)]" />
          <p className="mt-3 text-lg font-semibold">Mot de passe modifié ! Vous pouvez vous connecter.</p>
          <Link to="/connexion" className="btn-primary mt-6">Se connecter</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="rise card-paper mt-8 space-y-4 p-7">
          <input data-testid="reset-password-input" type="password" minLength={6} required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nouveau mot de passe (6 caractères min.)" className={inputCls} />
          <input data-testid="reset-password-confirm-input" type="password" minLength={6} required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirmer le mot de passe" className={inputCls} />
          {error && (
            <p data-testid="reset-error" className="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700">
              {error} {error.includes("expiré") && <Link to="/mot-de-passe-oublie" className="underline">Nouveau lien</Link>}
            </p>
          )}
          <button data-testid="reset-submit-button" disabled={busy} className="btn-primary w-full">{busy ? "Un instant…" : "Enregistrer"}</button>
        </form>
      )}
    </main>
  );
}
